<script lang="ts" setup>
/**
 * トークルーム・リプライ > 画像・動画表示。
 */

import { defineProps, reactive, ref, toRefs } from "vue";
import CustomModal from "../common/CustomModal.vue";
import { CommunityTalkImage, CommunityTalkMovie } from "@/apiRequests/models/communityTalk/talkRoomGetResponse";

interface Props {
	/**
	 * 画像情報。
	 */
	images: CommunityTalkImage[];
	/**
	 * 動画情報。
	 */
	movies: CommunityTalkMovie[];
}
defineProps<Props>();

interface State {
	/**
	 * モーダル表示対象のコンテンツ。
	 */
	modalMediaUrl: string | null;
	/**
	 * メディアの種別。
	 */
	modalMediaType: "image" | "movie";
}
// reactiveな変数宣言
const state = reactive<State>({
	modalMediaUrl: null,
	modalMediaType: "image",
});
const { modalMediaUrl, modalMediaType } = toRefs(state);

const modalComponent = ref<InstanceType<typeof CustomModal> | null>(null);
const openModal = (url: string, type: "image" | "movie"): void => {
	state.modalMediaUrl = url;
	state.modalMediaType = type;
	modalComponent.value?.openModal();
};
</script>

<template>
	<!-- 画像・動画がない場合は要素ごと表示しない -->
	<ul class="revico-community-talk-media" v-if="movies.length > 0 || images.length > 0">
		<!-- 動画 -->
		<li v-for="(movie, i) in movies" :key="i" class="revico-community-talk-media-movie">
			<img class="revico-community-talk-media-movie--thumbnail-img" :src="movie.thumbnailUrl" />
			<div class="revico-community-talk-media-movie--thumbnail-play-button" @click="openModal(movie.movieUrl, 'movie')"></div>
		</li>
		<!-- 画像 -->
		<li v-for="(image, i) in images" :key="i" class="revico-community-talk-media-image">
			<img class="revico-community-talk-media-image--img" :src="image.imageUrl" @click="openModal(image.imageUrl, 'image')" />
		</li>
	</ul>

	<CustomModal ref="modalComponent">
		<template #contents>
			<div v-if="modalMediaUrl" class="revico-community-talk-media-modal">
				<img v-if="modalMediaType === 'image'" :src="modalMediaUrl" class="revico-community-talk-media-modal--image" />
				<video v-if="modalMediaType === 'movie'" class="revico-community-talk-media-modal--movie" muted autoplay controls loop>
					<source :src="modalMediaUrl" />
					<p>動画を再生するには、videoタグをサポートしたブラウザが必要です。</p>
				</video>
			</div>
		</template>
	</CustomModal>
</template>

<style lang="scss">
.revico-widget .revico-community-talk {
	// 一覧
	&-media {
		display: flex;
		flex-wrap: wrap;
		align-items: center;
		margin-top: 12px;

		$max-height: 142px;
		&-movie,
		&-image {
			width: 31%;
			margin: 0 2% 2% 0;
			cursor: pointer;
			img {
				width: 100%;
				max-height: $max-height;
				object-fit: contain;
			}
		}
		&-movie {
			position: relative;
			&--thumbnail-play-button {
				@include moviePlayButton;
			}
		}
	}
	// モーダル
	&-media-modal {
		&--image {
			margin: auto;
			width: 100%;
			max-height: 72vh;
			object-fit: contain;
		}
		&--movie {
			margin: auto;
			width: 100%;
			max-width: 1000px;
			max-height: 72vh;
			min-height: 300px;
			max-height: 400px;
		}
	}
}

/* PC対応 */
@media screen and (min-width: 769px) {
	.revico-widget .revico-community-talk {
	}
}
</style>
